/**
 * PreSnapIndicator - Renders snap point indicator
 *
 * Displays a circle at the snap point with an optional perpendicular line
 * for motion indicators (not shown for shift).
 *
 * Pure visual component - no state or handlers.
 */

import React from 'react'
import type { Point } from '@/components/blocks/Types'

interface PreSnapIndicatorProps {
	position: Point
	motionType: 'motion' | 'shift'
	opacity?: number
	/** Path tangent angle in radians (from getPathTangentAtPoint utility) */
	tangentAngle?: number
}

/**
 * Calculate perpendicular line endpoints based on path tangent
 */
function getPerpendicularLine(position: Point, tangentAngle: number = 0, length: number = 1.5) {
	// Perpendicular is 90 degrees (PI/2) from tangent
	const perpAngle = tangentAngle + Math.PI / 2
	const dx = Math.cos(perpAngle) * length
	const dy = Math.sin(perpAngle) * length
	return {
		x1: position.x - dx,
		y1: position.y - dy,
		x2: position.x + dx,
		y2: position.y + dy,
	}
}

/**
 * Renders pre-snap indicator at snap point
 *
 * - Circle: Marks the snap point
 * - Perpendicular line: Shows motion direction (motion only, not shift)
 */
export function PreSnapIndicator({
	position,
	motionType,
	opacity = 1,
	tangentAngle = 0,
}: PreSnapIndicatorProps) {
	const perp = getPerpendicularLine(position, tangentAngle)

	return (
		<g className="pre-snap-indicator" opacity={opacity}>
			{/* Indicator circle */}
			<circle
				cx={position.x}
				cy={position.y}
				r={0.8}
				fill="var(--primary)"
				stroke="var(--primary-foreground)"
				strokeWidth={0.15}
			/>

			{/* Perpendicular line (motion only) */}
			{motionType === 'motion' && (
				<line
					x1={perp.x1}
					y1={perp.y1}
					x2={perp.x2}
					y2={perp.y2}
					stroke="var(--primary-foreground)"
					strokeWidth={0.2}
				/>
			)}
		</g>
	)
}
